import { type Component, defineAsyncComponent, markRaw } from "vue";
import { SQDCTileTypes } from "@/graphql";
import { isModuleActive } from "@/functions/moduleConfig";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";

export type TileConfigElementType = {
  active: boolean | ((siteId?: number) => boolean);
  settingsModule: Component;
  settingsProps?: Record<string, any>;
  component: Component;
  icon: string;
};

export type TileConfigType = Partial<Record<SQDCTileTypes, TileConfigElementType>>;

let licenseTileConfigs: TileConfigType = {};
const modules: Record<string, { tileConfigs: TileConfigType }> = import.meta.glob(
  "../../license/*/tileConfigs.ts",
  {
    eager: true,
  },
);
Object.entries(modules).forEach(([path, module]) => {
  if (isModuleActive(path.slice(2).split("/")[3])) {
    licenseTileConfigs = { ...licenseTileConfigs, ...module.tileConfigs };
  }
});

let moduleTileConfigs: TileConfigType = {};
const moduleTiles: Record<string, { tileConfigs: TileConfigType }> = import.meta.glob(
  "/**/modules/*/tileConfigs.ts",
  {
    eager: true,
  },
);
Object.entries(moduleTiles).forEach(([_path, module]) => {
  moduleTileConfigs = { ...moduleTileConfigs, ...module.tileConfigs };
});

export const tileConfigs: TileConfigType = {
  DepartmentsTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(
        () => import("@/modules/tiles/components/departmentsTile/DepartmentsTileSettings.vue"),
      ),
    ),
    component: markRaw(
      defineAsyncComponent(
        () => import("@/modules/tiles/components/departmentsTile/DepartmentsTile.vue"),
      ),
    ),
    icon: "fas fa-sitemap",
  },
  VitalityTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(
        () => import("@/components/tiles/settingsComponents/VitalityTileSettings.vue"),
      ),
    ),
    component: markRaw(
      defineAsyncComponent(
        () => import("@/modules/tiles/components/vitalityTile/VitalityTile.vue"),
      ),
    ),
    icon: "fas fa-heart-pulse",
  },

  TextTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(
        () => import("@/components/tiles/settingsComponents/TextTileSettings.vue"),
      ),
    ),
    component: markRaw(
      defineAsyncComponent(() => import("@/modules/tiles/components/textTile/TextTile.vue")),
    ),
    icon: "fas fa-font",
  },
  SQDCKeyTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/keyTile/KeyTileSettings.vue")),
    ),
    settingsProps: {
      type: SQDCTileTypes.SQDCKeyTile,
    },
    component: markRaw(defineAsyncComponent(() => import("./components/keyTile/KeyTile.vue"))),
    icon: "fas fa-dot-circle",
  },
  SQDCFlagKeyTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/keyTile/KeyTileSettings.vue")),
    ),
    settingsProps: {
      type: SQDCTileTypes.SQDCFlagKeyTile,
    },
    component: markRaw(defineAsyncComponent(() => import("./components/keyTile/KeyTile.vue"))),
    icon: "fas fa-flag",
  },
  SQDCKeyTileCheck: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/keyTile/KeyTileSettings.vue")),
    ),
    settingsProps: {
      type: SQDCTileTypes.SQDCKeyTileCheck,
    },
    component: markRaw(defineAsyncComponent(() => import("./components/keyTile/KeyTile.vue"))),
    icon: "fas fa-check",
  },
  DocumentTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(
        () => import("../filemanagement/components/FMDocumentTile/FMDocumentTileSettings.vue"),
      ),
    ),
    component: markRaw(
      defineAsyncComponent(
        () => import("../filemanagement/components/FMDocumentTile/FMDocumentTile.vue"),
      ),
    ),
    icon: "fas fa-images",
  },
  DashListTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/dashListTile/DashlistSettings.vue")),
    ),
    component: markRaw(
      defineAsyncComponent(() => import("./components/dashListTile/DashListTile.vue")),
    ),
    icon: "fas fa-tasks",
  },
  CalendarTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/calendarTile/CalendarTileSettings.vue")),
    ),
    component: markRaw(
      defineAsyncComponent(() => import("./components/calendarTile/CalendarTile.vue")),
    ),
    icon: "fas fa-calendar-alt",
  },
  IFrameTile: {
    active: true,
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/iFrameTile/IFrameTileSettings.vue")),
    ),
    component: markRaw(
      defineAsyncComponent(() => import("./components/iFrameTile/IFrameTile.vue")),
    ),
    icon: "fas fa-person-through-window",
  },
  VoteTile: {
    active: isModifierSet(ModifierType.LISA3),
    settingsModule: markRaw(
      defineAsyncComponent(() => import("./components/voteTile/VoteTileSettings.vue")),
    ),
    component: markRaw(defineAsyncComponent(() => import("./components/voteTile/VoteTile.vue"))),
    icon: "fas fa-martini-glass-citrus",
  },
  ...moduleTileConfigs,
  ...licenseTileConfigs,
};

export function getTileIcon(tileType: SQDCTileTypes) {
  return tileConfigs[tileType]?.icon ?? "fas fa-question";
}
